import { Part, tuning, type Volume } from "@meleeroyale/sim";
import * as THREE from "three";
import type { Telegraph } from "../net/game-client.ts";

const UP = new THREE.Vector3(0, 1, 0);
const BONE = 0xe8e4dc;

/** A procedural mannequin: one mesh per skeleton volume plus accent fists, feet and a headband. */
export class FighterMesh {
  readonly group = new THREE.Group();
  private readonly segments = new Map<number, THREE.Mesh>();
  private readonly head: THREE.Mesh;
  private readonly fistL: THREE.Mesh;
  private readonly fistR: THREE.Mesh;
  private readonly footL: THREE.Mesh;
  private readonly footR: THREE.Mesh;
  private readonly band: THREE.Mesh;
  private readonly shadow: THREE.Mesh;
  private readonly nameplate: THREE.Sprite;
  private readonly bodyMaterial: THREE.MeshToonMaterial;
  private readonly accentMaterial: THREE.MeshToonMaterial;
  private readonly tmpDir = new THREE.Vector3();
  private readonly tmpMid = new THREE.Vector3();

  constructor(accent: string, nickname: string) {
    const b = tuning.body;
    this.bodyMaterial = new THREE.MeshToonMaterial({ color: BONE });
    this.accentMaterial = new THREE.MeshToonMaterial({ color: new THREE.Color(accent) });
    const capsule = (length: number, radius: number, part: number) => {
      const mesh = new THREE.Mesh(
        new THREE.CapsuleGeometry(radius, length, 4, 10),
        this.bodyMaterial,
      );
      this.segments.set(part, mesh);
      this.group.add(mesh);
      return mesh;
    };
    capsule(b.shoulderY - b.hipY, b.torsoRadius, Part.Torso);
    capsule(b.upperArmLength, b.armRadius, Part.UpperArmL);
    capsule(b.upperArmLength, b.armRadius, Part.UpperArmR);
    capsule(b.forearmLength, b.armRadius, Part.ForearmL);
    capsule(b.forearmLength, b.armRadius, Part.ForearmR);
    capsule(b.thighLength, b.legRadius, Part.ThighL);
    capsule(b.thighLength, b.legRadius, Part.ThighR);
    capsule(b.shinLength, b.legRadius, Part.ShinL);
    capsule(b.shinLength, b.legRadius, Part.ShinR);

    this.head = new THREE.Mesh(new THREE.SphereGeometry(b.headRadius, 14, 10), this.bodyMaterial);
    this.band = new THREE.Mesh(
      new THREE.TorusGeometry(b.headRadius + 0.005, 0.02, 6, 20),
      this.accentMaterial,
    );
    this.band.rotation.x = Math.PI / 2;
    this.fistL = new THREE.Mesh(new THREE.SphereGeometry(b.fistRadius, 10, 8), this.accentMaterial);
    this.fistR = new THREE.Mesh(new THREE.SphereGeometry(b.fistRadius, 10, 8), this.accentMaterial);
    this.footL = new THREE.Mesh(
      new THREE.SphereGeometry(b.footRadius * 0.8, 10, 8),
      this.accentMaterial,
    );
    this.footR = new THREE.Mesh(
      new THREE.SphereGeometry(b.footRadius * 0.8, 10, 8),
      this.accentMaterial,
    );
    this.shadow = new THREE.Mesh(
      new THREE.CircleGeometry(0.42, 20),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.35 }),
    );
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.position.y = 0.01;
    this.nameplate = makeNameplate(nickname, accent);
    this.group.add(
      this.head,
      this.band,
      this.fistL,
      this.fistR,
      this.footL,
      this.footR,
      this.shadow,
      this.nameplate,
    );
  }

  setNickname(nickname: string, accent: string): void {
    const sprite = makeNameplate(nickname, accent);
    this.nameplate.material = sprite.material;
    this.nameplate.scale.copy(sprite.scale);
  }

  /** Places every mesh from the skeleton's world-space volumes. */
  pose(
    volumes: Volume[],
    alive: boolean,
    guard: boolean,
    exhausted: boolean,
    telegraph?: Telegraph,
  ): void {
    this.group.visible = true;
    for (const v of volumes) {
      const mesh = this.segments.get(v.part);
      if (!mesh) continue;
      this.tmpDir.set(v.b.x - v.a.x, v.b.y - v.a.y, v.b.z - v.a.z);
      const len = this.tmpDir.length();
      this.tmpMid.set((v.a.x + v.b.x) / 2, (v.a.y + v.b.y) / 2, (v.a.z + v.b.z) / 2);
      mesh.position.copy(this.tmpMid);
      if (len > 1e-6) {
        this.tmpDir.divideScalar(len);
        mesh.quaternion.setFromUnitVectors(UP, this.tmpDir);
      }
    }
    const head = volumes[Part.Head];
    if (head) {
      this.head.position.set(head.a.x, head.a.y, head.a.z);
      this.band.position.set(head.a.x, head.a.y + 0.04, head.a.z);
    }
    const fl = volumes[Part.ForearmL];
    const fr = volumes[Part.ForearmR];
    const sl = volumes[Part.ShinL];
    const sr = volumes[Part.ShinR];
    if (fl) this.fistL.position.set(fl.b.x, fl.b.y, fl.b.z);
    if (fr) this.fistR.position.set(fr.b.x, fr.b.y, fr.b.z);
    if (sl) this.footL.position.set(sl.b.x, sl.b.y + 0.03, sl.b.z);
    if (sr) this.footR.position.set(sr.b.x, sr.b.y + 0.03, sr.b.z);
    const torso = volumes[Part.Torso];
    if (torso) {
      this.shadow.position.set(torso.b.x, 0.01, torso.b.z);
      this.nameplate.position.set(torso.a.x, (head?.a.y ?? 1.7) + 0.35, torso.a.z);
    }
    this.bodyMaterial.color.setHex(alive ? (exhausted ? 0xd9c9b8 : BONE) : 0x6e6a66);
    const t = telegraph;
    const charge = t?.chargeLevel ?? 0;
    const grow = (limb: number) => (t?.chargeLimb === limb ? 1 + 0.7 * charge : 1);
    this.fistL.scale.setScalar(grow(0) * (t?.throwingArm ? 1.25 : 1));
    this.fistR.scale.setScalar(grow(1) * (t?.throwingArm ? 1.25 : 1));
    this.footL.scale.setScalar(grow(2) * (t?.throwingLeg ? 1.25 : 1));
    this.footR.scale.setScalar(grow(3) * (t?.throwingLeg ? 1.25 : 1));
    const glow =
      t?.throwingArm || t?.throwingLeg
        ? 0.9
        : t?.chargeLimb !== null && t !== undefined
          ? 0.2 + 0.5 * charge
          : guard
            ? 0.5
            : 0;
    this.accentMaterial.emissive.copy(this.accentMaterial.color).multiplyScalar(glow);
    this.band.scale.setScalar(t?.staggered ? 1.4 : 1);
  }

  hide(): void {
    this.group.visible = false;
  }

  dispose(): void {
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) o.geometry.dispose();
    });
    this.bodyMaterial.dispose();
    this.accentMaterial.dispose();
  }
}

function makeNameplate(text: string, accent: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.font = "700 30px ui-sans-serif, system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(7,8,10,0.6)";
    const w = Math.min(240, ctx.measureText(text).width + 28);
    roundRect(ctx, 128 - w / 2, 8, w, 48, 12);
    ctx.fill();
    ctx.fillStyle = accent;
    ctx.fillText(text, 128, 33, 220);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(1.1, 0.275, 1);
  sprite.renderOrder = 10;
  return sprite;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
